/* eslint-disable max-lines */
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import SearchIcon from '@mui/icons-material/Search';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormHelperText,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { format, isValid } from 'date-fns';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { lotSummaryApi } from 'api/inventory';
import UNVERIFIED_REASON_OPTIONS from 'common/constants/unverifiedReasons';
import { getStateDisplayName } from 'store/utils/shipmentUtils';
import SYSTEM_DEFICIT from 'common/constants/inventoryLabels';
import RemoveReasonRows from './RemoveReasonRows';

const SUMMARY_CARD_SX = {
  bgcolor: '#eef1f8',
  borderLeft: '4px solid #031c57',
  px: 2,
  py: 1.75,
  mt: 1,
  mx: 2,
  display: 'grid',
  // All seven facts on one row: they are identity, not content, and a second row pushed the lot
  // list further down on every open. Widths are proportional to how long each value actually runs.
  gridTemplateColumns: '1.6fr 0.7fr 1.2fr 1.3fr 1.5fr 1fr 0.7fr',
  gap: 1.5,
  alignItems: 'start',
};

const CANCEL_WARNING_TEXT = `Are you sure you want to cancel this count?\nThe count data will be discarded and the item will be unlocked.`;

const SummaryCell = ({ label, value }) => (
  <Box>
    <Typography variant='caption' color='text.secondary'>
      {label}
    </Typography>
    <Typography variant='body2' fontWeight={600}>
      {value || '—'}
    </Typography>
  </Box>
);

SummaryCell.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
};
SummaryCell.defaultProps = { value: '' };

const VarianceChip = ({ variance }) => {
  if (variance === null || variance === undefined) return null;
  if (variance === 0)
    return (
      <Chip
        label='No change'
        size='small'
        sx={{ bgcolor: '#f0f0f0', color: '#555', fontWeight: 600 }}
      />
    );
  if (variance > 0)
    return (
      <Chip
        label={`+${variance}`}
        size='small'
        sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 700, border: '1px solid #2e7d32' }}
      />
    );
  return (
    <Chip
      label={`${variance}`}
      size='small'
      sx={{ bgcolor: '#fdecea', color: '#c62828', fontWeight: 700, border: '1px solid #c62828' }}
    />
  );
};

VarianceChip.propTypes = { variance: PropTypes.number };
VarianceChip.defaultProps = { variance: null };

const buildInitialLotState = (lot) => ({
  inventoryLotId: lot.inventoryLotId,
  lotNumber: lot.lotNumber,
  expirationDate: lot.expirationDate,
  systemQty: Number(lot.remainingQty),
  wipQty: Number(lot.wipQty ?? 0),
  // POM-6094 - carried so the counter can see a claim standing against the lot. NOT
  // folded into systemQty: the count reconciles physical stock against what inventory
  // believes it received, and the claim is the part it never received.
  unverifiedQty: Number(lot.unverifiedQty ?? 0),
  hasUnverifiedClaim: Boolean(lot.hasUnverifiedClaim),
  countedQty: '',
  notes: '',
  offageReasons: [{ qty: '', reasons: [] }],
  // Required when this lot carries an unverified balance, as in Manual Adjustment. See
  // computeUnverifiedClear for what it clears.
  unverifiedReason: '',
  // POM-6111 — supplied only when the lot has no BUD of its own.
  suppliedBud: null,
  touched: false,
});

// A lot the system already believes is empty. Shown so it can be counted back up, but never
// required — an item can carry many historically depleted lots and forcing a count on each
// one would make the screen unusable.
const isDepletedLot = (lotState) => lotState.systemQty <= 0;

const computeVariance = (lotState) => {
  const cq = parseFloat(lotState.countedQty);
  if (lotState.countedQty === '' || Number.isNaN(cq)) return null;
  return parseFloat((cq - lotState.systemQty).toFixed(4));
};

// Core keeps the same list in WRITE_OFF_REASON_CODES. They are hidden from the dropdown for now
// (see unverifiedReasons.js), but still handled here so turning them on needs no change below.
const WRITE_OFF_REASON_CODES = ['NEVER_EXISTED', 'BREAKAGE', 'DAMAGE'];

// How much of the lot's unverified balance this count clears, or null when the lot has none.
// Mirrors core's resolveCountUnverifiedClear, which derives the same figure and never takes one
// from the client, so the read-only box shows exactly what will be written.
//
//   write-off (never existed / breakage / damage) — the whole balance, whatever was counted.
//   stock was real (found on shelf / missed receipt / …) — up to the surplus counted, since the
//     surplus is the only evidence a count has that the claim was real. 0 with no surplus: the
//     claim stays open for a later correction.
const computeUnverifiedClear = (lotState) => {
  if (!(lotState.unverifiedQty > 0)) return null;
  if (WRITE_OFF_REASON_CODES.includes(lotState.unverifiedReason)) {
    return { writeOff: true, qty: lotState.unverifiedQty };
  }
  const variance = computeVariance(lotState);
  return {
    writeOff: false,
    qty: variance !== null && variance > 0 ? Math.min(variance, lotState.unverifiedQty) : 0,
  };
};

// True when the whole counted surplus goes to settling the claim. The required reason already
// explains the increase, so the counter is not also asked for notes; a generated note is sent
// instead (buildSettlementNote), because core requires notes on any count increase. A surplus
// LARGER than the claim leaves genuinely new stock, which still needs notes.
const isSurplusExplainedByClaim = (lotState) => {
  const variance = computeVariance(lotState);
  const clear = computeUnverifiedClear(lotState);
  return (
    variance !== null && variance > 0 && Boolean(clear) && !clear.writeOff && clear.qty >= variance
  );
};

const buildSettlementNote = (lotState) => {
  const variance = computeVariance(lotState);
  const label =
    UNVERIFIED_REASON_OPTIONS.find((o) => o.code === lotState.unverifiedReason)?.label ??
    lotState.unverifiedReason;
  return `Counted ${variance} over the system qty, all already dispensed against the ${SYSTEM_DEFICIT.toLowerCase()} claim (${label}): ${SYSTEM_DEFICIT.toLowerCase()} ${lotState.unverifiedQty} -> ${lotState.unverifiedQty - variance}, no stock added.`;
};

// The lot's remaining qty once the count is saved. Units that settle the claim do not stay in
// stock — core takes them back off, as Manual Adjustment does for "already dispensed" — so the
// shelf keeps only the counted qty minus the settled part.
const computeResultingQty = (lotState) => {
  const clear = computeUnverifiedClear(lotState);
  const settled = clear && !clear.writeOff ? clear.qty : 0;
  return parseFloat((parseFloat(lotState.countedQty) - settled).toFixed(4));
};

const needsUnverifiedReason = (lotState) => {
  const variance = computeVariance(lotState);
  return lotState.unverifiedQty > 0 && variance !== null && variance > 0;
};

const isLotValid = (lotState) => {
  const cq = parseFloat(lotState.countedQty);
  if (lotState.countedQty === '' || Number.isNaN(cq) || cq < 0) return false;

  // A reason is required when the count finds a surplus on a lot carrying an unverified balance,
  // because that surplus settles the claim (as "already dispensed" does on Manual Adjustment's
  // Add Stock). A count at or below the system qty leaves the claim alone and asks nothing.
  if (needsUnverifiedReason(lotState) && !lotState.unverifiedReason) return false;

  // POM-6111 — a lot created by a fulfilment claim has no BUD and stays non-dispensable until
  // someone supplies it. The person counting the shelf is holding the packaging, so it stays
  // required.
  if (!lotState.expirationDate && !isValid(lotState.suppliedBud)) return false;

  const variance = parseFloat((cq - lotState.systemQty).toFixed(4));
  if (variance > 0 && !lotState.notes?.trim() && !isSurplusExplainedByClaim(lotState)) {
    return false;
  }
  if (variance < 0) {
    if (!lotState.offageReasons || lotState.offageReasons.length === 0) return false;
    const hasEmptyRow = lotState.offageReasons.some(
      (r) => r.reasons.length === 0 || !r.qty || parseFloat(r.qty) <= 0
    );
    if (hasEmptyRow) return false;
    const allocated = lotState.offageReasons.reduce((s, r) => s + (parseFloat(r.qty) || 0), 0);
    if (Math.abs(allocated - Math.abs(variance)) > 0.0001) return false;
  }
  return true;
};

// Returns 'untouched' | 'in_progress' | 'completed'
const getLotStatus = (lotState) => {
  if (lotState.countedQty === '') return 'untouched';
  const cq = parseFloat(lotState.countedQty);
  if (Number.isNaN(cq) || cq < 0) return 'in_progress';
  return isLotValid(lotState) ? 'completed' : 'in_progress';
};

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const ManageCountDialog = ({
  open,
  onClose,
  onSubmit,
  onCancel,
  itemData,
  submitLoading,
  cancelLoading,
}) => {
  const [lotsLoading, setLotsLoading] = useState(false);
  const [lotsError, setLotsError] = useState(null);
  const [lotStates, setLotStates] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [lotSearch, setLotSearch] = useState('');

  useEffect(() => {
    let cancelled = false;

    if (open && itemData?.inventoryItemId) {
      setLotsLoading(true);
      setLotsError(null);
      setLotStates([]);
      setSelectedIndex(0);
      setShowCancelConfirm(false);
      setLotSearch('');

      lotSummaryApi({
        inventoryItemId: itemData.inventoryItemId,
        ndc: itemData.ndc,
        fulfillingPharmacyId: itemData.fulfillingPharmacyId,
        // Depleted lots are included so stock found on the shelf can be counted back onto a
        // lot the system believes is empty. They are optional to fill in — see isDepletedLot.
        filter: { includeDepleted: true, includeExpired: true, includeDepletedExpired: true },
      })
        .then((summary) => {
          if (cancelled) return;
          const lots = summary?.lots ?? [];
          setLotStates(lots.map(buildInitialLotState));
        })
        .catch(() => {
          if (cancelled) return;
          setLotsError('Failed to load lot data. Please close and try again.');
        })
        .finally(() => {
          if (cancelled) return;
          setLotsLoading(false);
        });
    }

    return () => {
      cancelled = true;
    };
  }, [open, itemData?.inventoryItemId, itemData?.ndc, itemData?.fulfillingPharmacyId]);

  const handleLotChange = useCallback((index, partial) => {
    setLotStates((prev) =>
      prev.map((ls, i) => (i === index ? { ...ls, ...partial, touched: true } : ls))
    );
  }, []);

  const handleClearLot = useCallback((index) => {
    setLotStates((prev) =>
      prev.map((ls, i) =>
        i === index
          ? {
              ...ls,
              countedQty: '',
              notes: '',
              offageReasons: [{ qty: '', reasons: [] }],
              unverifiedReason: '',
              suppliedBud: null,
              touched: false,
            }
          : ls
      )
    );
  }, []);

  const loading = submitLoading || cancelLoading;
  // A count may be submitted with only some lots entered. The backend applies the variance of the
  // lots it receives on top of the item's system qty, so lots left blank are not touched, not
  // zeroed. Requiring every stocked lot made the screen unusable on items with hundreds of lots,
  // where the user only ever needs to correct the handful that are off.
  //
  // A lot that was STARTED but is not valid yet still blocks submission: dropping half-entered
  // work would silently discard a count the user believes they made. Untouched lots are skipped,
  // unfinished ones are not.
  const unfinishedCount = lotStates.filter((ls) => getLotStatus(ls) === 'in_progress').length;
  const canSubmit =
    unfinishedCount === 0 && lotStates.some((ls) => getLotStatus(ls) === 'completed');

  const totalSystemQty = lotStates.reduce((s, ls) => s + ls.systemQty, 0);
  const completedCount = lotStates.filter((ls) => getLotStatus(ls) === 'completed').length;
  const totalCountedQty = lotStates
    .filter((ls) => getLotStatus(ls) === 'completed')
    .reduce((s, ls) => s + parseFloat(ls.countedQty || 0), 0);
  const netVariance = parseFloat(
    lotStates
      .filter((ls) => getLotStatus(ls) === 'completed')
      // Net of any settlement: this is how much stock the count actually adds or removes.
      .reduce((s, ls) => s + parseFloat((computeResultingQty(ls) - ls.systemQty).toFixed(4)), 0)
      .toFixed(4)
  );

  const handleSubmit = () => {
    if (!canSubmit) return;
    const lotCounts = lotStates
      .filter((ls) => getLotStatus(ls) === 'completed')
      .map((ls) => {
        const variance = parseFloat((parseFloat(ls.countedQty) - ls.systemQty).toFixed(4));
        const entry = {
          inventoryLotId: ls.inventoryLotId,
          countedQty: parseFloat(ls.countedQty),
        };
        if (isSurplusExplainedByClaim(ls)) entry.notes = buildSettlementNote(ls);
        else if (ls.notes?.trim()) entry.notes = ls.notes.trim();
        // POM-6111 — both are GraphQL-typed (enum and Date), so a blank string is a request-level
        // validation error rather than a null: they have to be omitted, not blanked.
        if (!ls.expirationDate && isValid(ls.suppliedBud)) {
          entry.expirationDate = format(ls.suppliedBud, 'yyyy-MM-dd');
        }
        // Only the reason is sent, and only when it clears something: core derives the quantity
        // the same way computeUnverifiedClear does, and refuses a "stock was real" reason when
        // the count found no surplus to settle from.
        if (ls.unverifiedReason && computeUnverifiedClear(ls)?.qty > 0) {
          entry.reasonCode = ls.unverifiedReason;
        }
        if (variance < 0 && ls.offageReasons?.length > 0) {
          entry.offageReasons = ls.offageReasons
            .filter((r) => r.reasons.length > 0 && parseFloat(r.qty) > 0)
            .map((r) => ({ qty: parseFloat(r.qty), reasons: r.reasons }));
        }
        return entry;
      });
    onSubmit({ inventoryItemId: itemData?.inventoryItemId, lotCounts });
  };

  // Filter for display only — the original index is carried through so every handler
  // (selection, edit, clear) keeps addressing the right entry in lotStates.
  const visibleLots = useMemo(() => {
    const indexed = lotStates.map((lotState, index) => ({ lotState, index }));
    const query = lotSearch.trim().toLowerCase();
    if (!query) return indexed;
    return indexed.filter(({ lotState }) =>
      (lotState.lotNumber ?? '').toLowerCase().includes(query)
    );
  }, [lotStates, lotSearch]);

  const handleCancelCount = () => {
    onCancel({ inventoryItemId: itemData?.inventoryItemId });
  };

  const renderLotListRow = (lotState, index) => {
    const variance = computeVariance(lotState);
    const isSelected = selectedIndex === index;
    const status = getLotStatus(lotState);
    const unverifiedClearForRow = computeUnverifiedClear(lotState);

    let rowBorderColor = 'divider';
    let rowBorderLeftColor = '#c5cce0';
    let rowBgColor = 'background.paper';

    if (isSelected) {
      rowBorderColor = '#031c57';
      rowBorderLeftColor = '#031c57';
      rowBgColor = '#dce4f5';
    } else if (status === 'completed') {
      rowBorderColor = '#2e7d32';
      rowBorderLeftColor = '#2e7d32';
      rowBgColor = '#f0faf0';
    } else if (status === 'in_progress') {
      rowBorderLeftColor = '#ed6c02';
    }

    let circleColor = '#9e9e9e';
    if (status === 'in_progress') circleColor = '#ed6c02';
    if (isSelected && status !== 'completed') circleColor = '#031c57';

    return (
      <Box
        key={lotState.inventoryLotId}
        onClick={() => setSelectedIndex(index)}
        sx={{
          p: 2,
          mb: 1,
          borderRadius: 1,
          cursor: 'pointer',
          border: '1px solid',
          borderLeft: '4px solid',
          borderColor: rowBorderColor,
          borderLeftColor: rowBorderLeftColor,
          bgcolor: rowBgColor,
          transition: 'border-color 0.15s, background-color 0.15s',
          '&:hover': { bgcolor: status === 'completed' ? '#e8f5e9' : '#dce4f5' },
        }}
      >
        <Box
          sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.25 }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            {status === 'completed' ? (
              <CheckCircleIcon sx={{ color: '#2e7d32', fontSize: 16 }} />
            ) : (
              <RadioButtonUncheckedIcon sx={{ color: circleColor, fontSize: 16 }} />
            )}
            <Typography
              variant='body2'
              fontWeight={700}
              sx={{
                fontFamily: 'monospace',
                fontSize: 13,
                color: isSelected && status !== 'completed' ? '#031c57' : 'text.primary',
              }}
            >
              {lotState.lotNumber}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            {status === 'completed' && (
              <Chip
                label='Completed'
                size='small'
                sx={{
                  bgcolor: '#e8f5e9',
                  color: '#2e7d32',
                  fontWeight: 700,
                  fontSize: 10,
                  height: 20,
                }}
              />
            )}
            {status === 'in_progress' && (
              <Chip
                label='In Progress'
                size='small'
                sx={{
                  bgcolor: '#fff3e0',
                  color: '#e65100',
                  fontWeight: 700,
                  fontSize: 10,
                  height: 20,
                }}
              />
            )}
            {lotState.hasUnverifiedClaim && (
              <Chip
                label={SYSTEM_DEFICIT}
                size='small'
                sx={{
                  bgcolor: '#fff3e0',
                  color: '#e65100',
                  fontWeight: 700,
                  fontSize: 10,
                  height: 20,
                }}
              />
            )}
            {status === 'untouched' && isDepletedLot(lotState) && (
              <Chip
                label='Depleted'
                size='small'
                sx={{
                  bgcolor: '#f0f0f0',
                  color: '#555',
                  fontWeight: 700,
                  fontSize: 10,
                  height: 20,
                }}
              />
            )}
            {isSelected && (
              <Typography sx={{ color: '#031c57', fontWeight: 700, fontSize: 16, lineHeight: 1 }}>
                ›
              </Typography>
            )}
          </Box>
        </Box>
        <Typography variant='caption' color='text.secondary' display='block'>
          BUD: {formatDate(lotState.expirationDate)} · Sys:{' '}
          {status === 'completed'
            ? `${lotState.systemQty} → ${computeResultingQty(lotState)}`
            : lotState.systemQty}
          {lotState.hasUnverifiedClaim && (
            <Box component='span' sx={{ color: 'warning.dark', fontWeight: 700 }}>
              {` · ${SYSTEM_DEFICIT}: ${lotState.unverifiedQty}`}
              {status === 'completed' &&
                unverifiedClearForRow?.qty > 0 &&
                ` → ${
                  lotState.unverifiedQty - unverifiedClearForRow.qty > 0
                    ? lotState.unverifiedQty - unverifiedClearForRow.qty
                    : '0'
                }`}
            </Box>
          )}
        </Typography>
        {status === 'completed' && variance !== null && variance !== 0 && (
          <Box sx={{ mt: 0.5 }}>
            <VarianceChip variance={variance} />
          </Box>
        )}
      </Box>
    );
  };

  const renderEditPanel = (lotState, index) => {
    const variance = computeVariance(lotState);
    const showOffage = variance !== null && variance < 0;
    const showNotes = variance !== null && variance > 0 && !isSurplusExplainedByClaim(lotState);
    const unverifiedClear = computeUnverifiedClear(lotState);
    // The reason and the read-only box only appear once the count finds a surplus: that is the
    // only case in which the count settles anything.
    const showUnverifiedReason = needsUnverifiedReason(lotState);
    const unverifiedReasonMissing = showUnverifiedReason && !lotState.unverifiedReason;
    const cqNum = parseFloat(lotState.countedQty);
    const cqBlank = lotState.countedQty === '';
    const isDepleted = isDepletedLot(lotState);
    const cqInvalid =
      lotState.touched &&
      ((cqBlank && !isDepleted) || (!cqBlank && (Number.isNaN(cqNum) || cqNum < 0)));
    let cqHelperText = '';
    if (cqInvalid) cqHelperText = 'Required, min 0';
    else if (isDepleted) cqHelperText = 'Optional — leave blank if no stock was found';

    const offageAllocated = lotState.offageReasons.reduce(
      (s, r) => s + (parseFloat(r.qty) || 0),
      0
    );
    const offageComplete = showOffage && Math.abs(offageAllocated - Math.abs(variance)) <= 0.0001;
    // "Started" = the user has begun splitting the shortfall, so an incomplete split is now
    // genuinely their unfinished work rather than a prompt they have not read yet.
    const offageStarted = lotState.offageReasons.some((r) => r.qty !== '' || r.reasons.length > 0);

    return (
      <Box sx={{ p: 3 }}>
        {/* Lot header + Clear button */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            mb: 2.5,
          }}
        >
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2, flex: 1 }}>
            {[
              { label: 'LOT', value: lotState.lotNumber, mono: true },
              { label: 'BUD', value: formatDate(lotState.expirationDate) },
              { label: 'SYSTEM QTY', value: String(lotState.systemQty) },
            ].map((cell) => (
              <Box key={cell.label}>
                <Typography
                  variant='caption'
                  display='block'
                  sx={{
                    color: 'text.secondary',
                    fontWeight: 600,
                    letterSpacing: 0.5,
                    textTransform: 'uppercase',
                    fontSize: 10,
                    mb: 0.25,
                  }}
                >
                  {cell.label}
                </Typography>
                <Typography
                  variant='body2'
                  fontWeight={700}
                  sx={cell.mono ? { fontFamily: 'monospace' } : {}}
                >
                  {cell.value}
                </Typography>
              </Box>
            ))}
          </Box>
          <Button
            variant='text'
            size='small'
            onClick={() => handleClearLot(index)}
            sx={{ color: 'text.secondary', minWidth: 'auto', ml: 1.5, flexShrink: 0 }}
          >
            Clear
          </Button>
        </Box>

        <Divider sx={{ mb: 2.5 }} />

        {/* Counted qty + variance — vertically centred */}
        <Box sx={{ display: 'flex', gap: 2.5, alignItems: 'center', mb: 2.5 }}>
          <TextField
            label={isDepleted ? 'Counted Qty' : 'Counted Qty *'}
            size='small'
            type='number'
            inputProps={{ min: 0, step: 'any' }}
            value={lotState.countedQty}
            onChange={(e) => handleLotChange(index, { countedQty: e.target.value })}
            onBlur={() => handleLotChange(index, { touched: true })}
            error={cqInvalid}
            helperText={cqHelperText}
            placeholder='Enter qty'
            sx={{
              width: 160,
              '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                WebkitAppearance: 'none',
              },
              '& input[type=number]': { MozAppearance: 'textfield' },
            }}
          />
          <Box>
            <Typography variant='caption' color='text.secondary' display='block' sx={{ mb: 0.5 }}>
              Variance
            </Typography>
            {variance !== null ? (
              <VarianceChip variance={variance} />
            ) : (
              <Typography variant='caption' color='text.disabled'>
                —
              </Typography>
            )}
          </Box>
          {/* Where the counted qty goes, once a surplus on a claimed lot settles part of it:
              what was already in stock, what settles the claim, and what is added on top. */}
          {showUnverifiedReason && (
            <Box
              sx={{
                display: 'flex',
                gap: 3,
                ml: 1,
                pl: 2.5,
                borderLeft: '1px solid',
                borderColor: 'divider',
              }}
              data-testid={`count-breakdown-${index}`}
            >
              {[
                { label: 'In stock', value: lotState.systemQty, testId: 'in-stock' },
                {
                  label: `Settles ${SYSTEM_DEFICIT.toLowerCase()}`,
                  value: unverifiedClear.qty,
                  testId: 'settles',
                },
                {
                  label: 'Added to stock',
                  value: parseFloat((variance - unverifiedClear.qty).toFixed(4)),
                  testId: 'added',
                },
              ].map((cell) => (
                <Box key={cell.label}>
                  <Typography
                    variant='caption'
                    color='text.secondary'
                    display='block'
                    sx={{ mb: 0.5 }}
                  >
                    {cell.label}
                  </Typography>
                  <Typography
                    variant='body2'
                    fontWeight={700}
                    data-testid={`count-breakdown-${cell.testId}-${index}`}
                  >
                    {cell.value}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </Box>

        {/* ── What a count owes this lot ──
            The unverified balance: the counter is told to leave the claimed units out of the
            count (they are committed to an order, not on the shelf), and must give a reason, as
            Manual Adjustment asks. The amount the count clears is shown read-only beside it,
            because it is derived, not chosen.

            The missing BUD is required. The counter is holding the packaging. Both sit above the
            variance-driven fields because they apply whatever the variance turns out to be,
            including zero. */}
        {unverifiedClear && (
          <Alert severity='info' icon={false} sx={{ mb: 2.5 }}>
            <Typography variant='subtitle2' sx={{ fontWeight: 700, mb: 0.5 }}>
              Count the shelf only
            </Typography>
            <Typography
              variant='caption'
              display='block'
              sx={{ mb: showUnverifiedReason ? 1.5 : 0 }}
              data-testid={`count-unverified-note-${index}`}
            >
              {lotState.unverifiedQty} unit{lotState.unverifiedQty === 1 ? '' : 's'} of this lot
              {lotState.unverifiedQty === 1 ? ' was' : ' were'} dispensed before inventory ever
              received {lotState.unverifiedQty === 1 ? 'it' : 'them'}. Units you count above the
              system qty settle this first, up to {lotState.unverifiedQty}; only what is left over
              is added to stock, and you will be asked why.
            </Typography>
            {showUnverifiedReason && (
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                <FormControl
                  size='small'
                  sx={{ flex: 1, bgcolor: 'background.paper' }}
                  error={unverifiedReasonMissing}
                >
                  <InputLabel id={`count-unverified-reason-label-${index}`}>Reason *</InputLabel>
                  <Select
                    labelId={`count-unverified-reason-label-${index}`}
                    label='Reason *'
                    value={lotState.unverifiedReason}
                    onChange={(e) => handleLotChange(index, { unverifiedReason: e.target.value })}
                    inputProps={{ 'data-testid': `count-unverified-reason-${index}` }}
                  >
                    {UNVERIFIED_REASON_OPTIONS.map((option) => (
                      <MenuItem key={option.code} value={option.code}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                  {unverifiedReasonMissing && <FormHelperText>Required</FormHelperText>}
                </FormControl>
                <TextField
                  type='number'
                  label='Already dispensed'
                  size='small'
                  disabled
                  sx={{ width: 180, bgcolor: 'background.paper' }}
                  value={String(unverifiedClear.qty)}
                  inputProps={{ readOnly: true, 'data-testid': `count-unverified-clear-${index}` }}
                  helperText={`of this lot's ${lotState.unverifiedQty} ${SYSTEM_DEFICIT.toLowerCase()}`}
                />
              </Box>
            )}
            {showUnverifiedReason && lotState.unverifiedReason && (
              <Typography
                variant='caption'
                display='block'
                sx={{ mt: 1 }}
                data-testid={`count-unverified-summary-${index}`}
              >
                {unverifiedClear.writeOff
                  ? `Writes off all ${unverifiedClear.qty} ${SYSTEM_DEFICIT.toLowerCase()} without adding stock — the ${SYSTEM_DEFICIT.toLowerCase()} balance drops to 0.`
                  : `Counts ${variance} over the system qty, of which ${
                      unverifiedClear.qty
                    } was already dispensed — remaining goes up by ${
                      variance - unverifiedClear.qty
                    } and the ${SYSTEM_DEFICIT.toLowerCase()} balance drops to ${
                      lotState.unverifiedQty - unverifiedClear.qty
                    }.`}
              </Typography>
            )}
          </Alert>
        )}

        {!lotState.expirationDate && (
          <Alert severity='warning' icon={false} sx={{ mb: 2.5 }}>
            <Typography variant='subtitle2' sx={{ fontWeight: 700, mb: 0.5 }}>
              This lot needs a date
            </Typography>
            <Typography variant='caption' display='block' sx={{ mb: 1 }}>
              This lot has no expiration date, so it cannot be dispensed. Supply the date from the
              packaging.
            </Typography>
            <DesktopDatePicker
              label='Beyond-Use Date *'
              inputFormat='MM/dd/yyyy'
              value={lotState.suppliedBud}
              onChange={(value) => handleLotChange(index, { suppliedBud: value })}
              renderInput={(params) => (
                <TextField
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  {...params}
                  size='small'
                  fullWidth
                  sx={{ bgcolor: 'background.paper' }}
                  error={lotState.touched && !isValid(lotState.suppliedBud)}
                  helperText={lotState.touched && !isValid(lotState.suppliedBud) ? 'Required' : ''}
                  inputProps={{
                    ...params.inputProps,
                    'data-testid': `count-supplied-bud-${index}`,
                  }}
                />
              )}
            />
          </Alert>
        )}

        {showOffage && (
          <Box sx={{ mb: 2.5 }}>
            {/* A shortfall needs reasons, but the user has only just typed the count — this is an
                instruction at that point, not a mistake. It stays neutral until they start
                allocating, and turns green once the split adds up. */}
            <Typography
              variant='caption'
              fontWeight={600}
              display='block'
              sx={{
                mb: 0.75,
                // eslint-disable-next-line no-nested-ternary
                color: offageComplete ? '#2e7d32' : offageStarted ? 'error.main' : 'text.secondary',
              }}
            >
              {offageComplete
                ? 'Offage Reasons ✓'
                : `Offage Reasons (required — must sum to ${Math.abs(variance)})`}
            </Typography>
            <RemoveReasonRows
              rows={lotState.offageReasons}
              onChange={(rows) => handleLotChange(index, { offageReasons: rows })}
              totalQty={Math.abs(variance)}
              submitted={false}
            />
          </Box>
        )}

        {showNotes && (
          <TextField
            label='Notes (required for increase)'
            size='small'
            fullWidth
            multiline
            rows={2}
            value={lotState.notes}
            onChange={(e) => handleLotChange(index, { notes: e.target.value })}
            error={lotState.touched && !lotState.notes?.trim()}
            helperText={
              lotState.touched && !lotState.notes?.trim()
                ? 'Notes are required when counted qty exceeds system qty'
                : ''
            }
            sx={{ mb: 2.5 }}
          />
        )}
      </Box>
    );
  };

  const renderSplitContent = () => {
    let netVarianceColor = 'text.primary';
    if (netVariance > 0) netVarianceColor = '#2e7d32';
    else if (netVariance < 0) netVarianceColor = '#c62828';

    if (lotsLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
          <CircularProgress size={32} />
        </Box>
      );
    }
    if (lotsError) {
      return (
        <Box sx={{ p: 2 }}>
          <Alert severity='error'>{lotsError}</Alert>
        </Box>
      );
    }
    if (lotStates.length === 0) {
      return (
        <Box sx={{ p: 2 }}>
          <Alert severity='info'>
            No lots found for this item. Receive stock to create a lot before counting.
          </Alert>
        </Box>
      );
    }

    return (
      <>
        {/* Split pane */}
        <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden', p: 2, gap: 2 }}>
          {/* Left — lot list */}
          <Box
            sx={{
              width: 300,
              minWidth: 300,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                px: 1.5,
                py: 0.875,
                borderBottom: '1px solid',
                borderColor: 'divider',
                bgcolor: '#f9f9fb',
              }}
            >
              <Typography
                variant='caption'
                fontWeight={700}
                color='text.secondary'
                sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: 10 }}
              >
                Lot List
                {lotSearch.trim() && ` — ${visibleLots.length} of ${lotStates.length}`}
              </Typography>

              {/* Scrolling to a lot is impractical once an item has hundreds of them, and the
                  user usually arrives knowing exactly which lot is off. */}
              {lotStates.length > 1 && (
                <TextField
                  size='small'
                  fullWidth
                  value={lotSearch}
                  onChange={(e) => setLotSearch(e.target.value)}
                  placeholder='Search lot number'
                  id='ManageCountDialog-lotSearch-input'
                  sx={{ mt: 0.875, bgcolor: 'background.paper' }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position='start'>
                        <SearchIcon sx={{ fontSize: '1rem', color: 'text.disabled' }} />
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            </Box>

            <Box data-testid='lot-list' sx={{ flex: 1, overflowY: 'auto', p: 1.25 }}>
              {visibleLots.length === 0 ? (
                <Typography
                  variant='caption'
                  color='text.secondary'
                  sx={{ display: 'block', textAlign: 'center', py: 3 }}
                >
                  No lot matches &quot;{lotSearch.trim()}&quot;
                </Typography>
              ) : (
                visibleLots.map(({ lotState, index }) => renderLotListRow(lotState, index))
              )}
            </Box>

            {/* Bottom summary */}
            <Box
              sx={{
                borderTop: '1px solid',
                borderColor: 'divider',
                p: 1.5,
                bgcolor: '#f9f9fb',
                flexShrink: 0,
              }}
            >
              {[
                { label: 'Total System Qty', value: totalSystemQty.toLocaleString() },
                {
                  label: 'Total Counted Qty',
                  value:
                    completedCount === lotStates.length
                      ? totalCountedQty.toLocaleString()
                      : `${totalCountedQty.toLocaleString()} (partial — ${completedCount} of ${lotStates.length} done)`,
                },
                {
                  label: 'Net Variance',
                  value: netVariance > 0 ? `+${netVariance}` : String(netVariance),
                  color: netVarianceColor,
                },
              ].map((row) => (
                <Box
                  key={row.label}
                  sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.4 }}
                >
                  <Typography variant='caption' color='text.secondary'>
                    {row.label}
                  </Typography>
                  <Typography
                    variant='caption'
                    fontWeight={700}
                    sx={{ color: row.color ?? 'text.primary' }}
                  >
                    {row.value}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>

          {/* Right — edit panel */}
          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
              bgcolor: '#fafbfc',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
            }}
          >
            {lotStates[selectedIndex] ? (
              renderEditPanel(lotStates[selectedIndex], selectedIndex)
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100%',
                }}
              >
                <Typography variant='body2' color='text.disabled'>
                  Select a lot from the list to begin counting
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth='lg'
      fullWidth
      PaperProps={{
        sx: { height: '85vh', maxHeight: 700, display: 'flex', flexDirection: 'column' },
      }}
    >
      <DialogTitle sx={{ pr: 6 }}>
        <Typography variant='h6' component='span' fontWeight={700}>
          Manage Physical Count
        </Typography>
        <IconButton
          onClick={onClose}
          disabled={loading}
          sx={{ position: 'absolute', right: 8, top: 8 }}
          aria-label='close'
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{ p: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
      >
        <Box sx={SUMMARY_CARD_SX}>
          <SummaryCell label='Drug Name' value={itemData?.drugName} />
          <SummaryCell label='Strength' value={itemData?.strength} />
          <SummaryCell label='NDC' value={itemData?.ndc} />
          <SummaryCell label='Manufacturer' value={itemData?.manufacturer} />
          <SummaryCell
            label='Facility'
            value={
              itemData?.facilityState
                ? `${itemData.facilityName} (${getStateDisplayName(itemData.facilityState)})`
                : itemData?.facilityName
            }
          />
          <SummaryCell
            label='System Qty (Locked)'
            value={lotsLoading ? '—' : totalSystemQty.toLocaleString()}
          />
          <SummaryCell
            label='Available Lots'
            value={lotsLoading ? '—' : String(lotStates.filter((ls) => !isDepletedLot(ls)).length)}
          />
        </Box>

        {showCancelConfirm ? (
          <Box sx={{ p: 2 }}>
            <Alert severity='warning' sx={{ whiteSpace: 'pre-line', mb: 2 }}>
              {CANCEL_WARNING_TEXT}
            </Alert>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
              <Button
                variant='text'
                onClick={() => setShowCancelConfirm(false)}
                disabled={cancelLoading}
              >
                No, Keep Count
              </Button>
              <Button
                variant='contained'
                onClick={handleCancelCount}
                disabled={cancelLoading}
                startIcon={cancelLoading ? <CircularProgress size={16} color='inherit' /> : null}
              >
                Yes, Cancel Count
              </Button>
            </Box>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
            {renderSplitContent()}
          </Box>
        )}
      </DialogContent>

      {!showCancelConfirm && (
        <DialogActions sx={{ justifyContent: 'space-between' }}>
          <Button
            variant='text'
            onClick={() => setShowCancelConfirm(true)}
            disabled={loading}
            sx={{ color: '#D97706' }}
          >
            Cancel Count
          </Button>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {/* Submitting closes the count and unlocks the item, so say plainly what happens to
                the lots left blank — they keep their current quantity, they are not zeroed. */}
            {canSubmit && completedCount < lotStates.length && (
              <Typography variant='caption' color='text.secondary' sx={{ mr: 0.5 }}>
                {lotStates.length - completedCount} lot
                {lotStates.length - completedCount === 1 ? '' : 's'} left uncounted — their
                quantities stay as they are
              </Typography>
            )}
            <Button variant='text' onClick={onClose} disabled={loading}>
              Close
            </Button>
            <Button
              variant='contained'
              color='primary'
              onClick={handleSubmit}
              disabled={lotsLoading || !!lotsError || !canSubmit || submitLoading}
              startIcon={submitLoading ? <CircularProgress size={16} color='inherit' /> : null}
            >
              Submit Count
            </Button>
          </Box>
        </DialogActions>
      )}
    </Dialog>
  );
};

ManageCountDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  itemData: PropTypes.shape({
    drugName: PropTypes.string,
    strength: PropTypes.string,
    ndc: PropTypes.string,
    manufacturer: PropTypes.string,
    facilityName: PropTypes.string,
    facilityState: PropTypes.string,
    inventoryItemId: PropTypes.string,
    fulfillingPharmacyId: PropTypes.string,
  }),
  submitLoading: PropTypes.bool,
  cancelLoading: PropTypes.bool,
};
ManageCountDialog.defaultProps = {
  itemData: null,
  submitLoading: false,
  cancelLoading: false,
};

export default ManageCountDialog;
